import { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Bot, Phone, MessageSquare, PhoneForwarded, Link as LinkIcon, Globe, Pencil, Check, X, Loader2 } from 'lucide-react';
import apiService from '../../services/api';

const AgentDetailsSection = ({ shop, shopId, canEdit, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedDetails, setEditedDetails] = useState(null);
  const [saving, setSaving] = useState(false);

  const agentDetails = shop.shopAgentDetails || {};

  const handleStartEdit = () => {
    setEditedDetails({
      agentName: agentDetails.agentName || '',
      phoneAgentNumber: agentDetails.phoneAgentNumber || '',
      smsAgentNumber: agentDetails.smsAgentNumber || '',
      faxNumber: agentDetails.faxNumber || '',
      agentLink: agentDetails.agentLink || '',
      websiteUrl: agentDetails.websiteUrl || shop.websiteLink || '',
      profileImage: agentDetails.profileImage || '',
    });
    setIsEditing(true);
  };

  const handleCancel = () => {
    setEditedDetails(null);
    setIsEditing(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (editedDetails.agentName?.trim()) payload.agentName = editedDetails.agentName.trim();
      if (editedDetails.phoneAgentNumber?.trim()) payload.phoneAgentNumber = editedDetails.phoneAgentNumber.trim();
      if (editedDetails.smsAgentNumber?.trim()) payload.smsAgentNumber = editedDetails.smsAgentNumber.trim();
      if (editedDetails.faxNumber?.trim()) payload.faxNumber = editedDetails.faxNumber.trim();
      if (editedDetails.agentLink?.trim()) payload.agentLink = editedDetails.agentLink.trim();
      if (editedDetails.websiteUrl?.trim()) payload.websiteUrl = editedDetails.websiteUrl.trim();
      if (editedDetails.profileImage?.trim()) payload.profileImage = editedDetails.profileImage.trim();

      const response = await apiService.updateShopAgentDetails(shopId, payload);
      
      if (response.success) {
        onUpdate({
          shopAgentDetails: response.data.shopAgentDetails || { ...agentDetails, ...payload },
          websiteLink: editedDetails.websiteUrl || shop.websiteLink,
        });
        setIsEditing(false);
        setEditedDetails(null);
      } else {
        alert(response.message || 'Failed to update agent details');
      }
    } catch (err) {
      alert(err.message || 'Failed to update agent details');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setEditedDetails(prev => ({ ...prev, [field]: value }));
  };

  return (
    <AccordionItem value="agent" className="bg-[#1E293B] border border-[#334155] rounded-lg overflow-hidden">
      <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-[#334155]/30" data-testid="agent-details-trigger">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-[#22C55E]/20 flex items-center justify-center">
            <Bot className="w-5 h-5 text-[#4ADE80]" />
          </div>
          <span className="text-lg font-semibold text-[#E2E8F0]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Shop Agent Details
          </span>
        </div>
      </AccordionTrigger>
      <AccordionContent className="px-6 pb-4">
        {canEdit && (
          <div className="flex justify-end mb-4">
            {isEditing ? (
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleCancel}
                  className="border-[#334155] text-[#94A3B8] hover:bg-[#334155]"
                >
                  <X className="w-4 h-4 mr-1" />
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-[#22C55E] hover:bg-[#16A34A] text-white"
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                  Save Changes
                </Button>
              </div>
            ) : (
              <Button
                size="sm"
                variant="outline"
                onClick={handleStartEdit}
                className="border-[#2563EB]/30 text-[#60A5FA] hover:bg-[#2563EB]/20"
              >
                <Pencil className="w-4 h-4 mr-1" />
                Edit Details
              </Button>
            )}
          </div>
        )}

        <div className="flex flex-col md:flex-row gap-6">
          <div className="flex flex-col items-center gap-3">
            <Avatar className="w-24 h-24 border-2 border-[#334155]">
              <AvatarImage src={agentDetails.profileImage} alt={agentDetails.agentName} />
              <AvatarFallback className="bg-[#334155] text-[#94A3B8] text-2xl">
                {agentDetails.agentName?.charAt(0) || 'A'}
              </AvatarFallback>
            </Avatar>
            <div className="text-center">
              {isEditing ? (
                <Input
                  value={editedDetails.agentName}
                  onChange={(e) => handleChange('agentName', e.target.value)}
                  className="bg-[#0F172A] border-[#334155] text-[#E2E8F0] text-center w-32"
                />
              ) : (
                <p className="text-[#E2E8F0] font-medium">{agentDetails.agentName || 'AI Agent'}</p>
              )}
              <p className="text-[#64748B] text-xs mt-1">AI Agent</p>
            </div>
          </div>
          
          <div className="flex-1 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-[#0F172A] border border-[#334155] rounded-lg p-4">
                <div className="flex items-center gap-2 text-[#64748B] text-xs mb-2">
                  <Phone className="w-4 h-4" />
                  Phone Agent Number
                </div>
                {isEditing ? (
                  <Input
                    value={editedDetails.phoneAgentNumber}
                    onChange={(e) => handleChange('phoneAgentNumber', e.target.value)}
                    className="bg-[#1E293B] border-[#334155] text-[#E2E8F0] h-9"
                  />
                ) : (
                  <p className="text-[#E2E8F0] font-medium">{agentDetails.phoneAgentNumber || 'N/A'}</p>
                )}
              </div>

              <div className="bg-[#0F172A] border border-[#334155] rounded-lg p-4">
                <div className="flex items-center gap-2 text-[#64748B] text-xs mb-2">
                  <MessageSquare className="w-4 h-4" />
                  SMS Agent Number
                </div>
                {isEditing ? (
                  <Input
                    value={editedDetails.smsAgentNumber}
                    onChange={(e) => handleChange('smsAgentNumber', e.target.value)}
                    className="bg-[#1E293B] border-[#334155] text-[#E2E8F0] h-9"
                  />
                ) : (
                  <p className="text-[#E2E8F0] font-medium">{agentDetails.smsAgentNumber || 'N/A'}</p>
                )}
              </div>

              <div className="bg-[#0F172A] border border-[#334155] rounded-lg p-4">
                <div className="flex items-center gap-2 text-[#64748B] text-xs mb-2">
                  <PhoneForwarded className="w-4 h-4" />
                  Fax Number
                </div>
                {isEditing ? (
                  <Input
                    value={editedDetails.faxNumber}
                    onChange={(e) => handleChange('faxNumber', e.target.value)}
                    className="bg-[#1E293B] border-[#334155] text-[#E2E8F0] h-9"
                  />
                ) : (
                  <p className="text-[#E2E8F0] font-medium">{agentDetails.faxNumber || 'N/A'}</p>
                )}
              </div>

              <div className="bg-[#0F172A] border border-[#334155] rounded-lg p-4">
                <div className="flex items-center gap-2 text-[#64748B] text-xs mb-2">
                  <LinkIcon className="w-4 h-4" />
                  Web Agent Link
                </div>
                {isEditing ? (
                  <Input
                    value={editedDetails.agentLink}
                    onChange={(e) => handleChange('agentLink', e.target.value)}
                    className="bg-[#1E293B] border-[#334155] text-[#E2E8F0] h-9"
                  />
                ) : agentDetails.agentLink ? (
                  <a href={agentDetails.agentLink} target="_blank" rel="noopener noreferrer" className="text-[#60A5FA] hover:text-[#93C5FD] text-sm break-all">
                    {agentDetails.agentLink}
                  </a>
                ) : (
                  <p className="text-[#64748B] text-sm">N/A</p>
                )}
              </div>

              <div className="bg-[#0F172A] border border-[#334155] rounded-lg p-4 sm:col-span-2">
                <div className="flex items-center gap-2 text-[#64748B] text-xs mb-2">
                  <Globe className="w-4 h-4" />
                  Website URL
                </div>
                {isEditing ? (
                  <Input
                    value={editedDetails.websiteUrl}
                    onChange={(e) => handleChange('websiteUrl', e.target.value)}
                    className="bg-[#1E293B] border-[#334155] text-[#E2E8F0] h-9"
                    placeholder="https://example.com"
                  />
                ) : (agentDetails.websiteUrl || shop.websiteLink) ? (
                  <a href={agentDetails.websiteUrl || shop.websiteLink} target="_blank" rel="noopener noreferrer" className="text-[#60A5FA] hover:text-[#93C5FD] text-sm break-all">
                    {agentDetails.websiteUrl || shop.websiteLink}
                  </a>
                ) : (
                  <p className="text-[#64748B] text-sm">No website</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </AccordionContent>
    </AccordionItem>
  );
};

export default AgentDetailsSection;
